import type { CabinetBranchAccess } from "@planerius/contracts/cabinet-auth"
import type { ReactNode } from "react"
import { useCallback, useMemo, useState } from "react"

import { cx } from "../lib/cx"
import styles from "./branch-switcher.module.css"
import { Popover } from "./popover"
import { formatShortZoneDiff } from "./timezone-note"
import { ALL_BRANCHES, BRANCH_SEARCH_FROM } from "./use-branch"
import type { BranchSelection } from "./use-branch"

/**
 * Переключатель филиала.
 *
 * ПОСТОЯННЫЙ ЭЛЕМЕНТ КАРКАСА, А НЕ НАСТРОЙКА ВНУТРИ РАЗДЕЛА (ТЗ §4.4). Сеть — норма,
 * а человек, который перепутал точку и записал клиента в другой город, — типичная авария.
 * Поэтому выбранный филиал виден всегда, в том числе на узком экране и на телефоне.
 *
 * ОДИН ФИЛИАЛ — ПЕРЕКЛЮЧАТЕЛЯ НЕТ, НАЗВАНИЕ ЕСТЬ. Кнопка, которая ничего не переключает,
 * только предлагает нажать на себя и убедиться, что выбирать не из чего.
 *
 * «ВСЕ ФИЛИАЛЫ» ПОКАЗЫВАЕТСЯ ВСЕГДА, НО НЕ ВЕЗДЕ РАБОТАЕТ. В журнале режима «все точки»
 * нет и быть не может: день двух филиалов в разных поясах в одной сетке нечитаем.
 * Строка при этом не исчезает, а выключается с причиной — по общему правилу ТЗ §3.6:
 * прячут то, чего не будет никогда, а выключенным с пояснением показывают то, что
 * существует и работает в соседнем разделе. Иначе владелец, у которого «Все филиалы»
 * то есть, то нет, решит, что кабинет забыл его выбор.
 *
 * ДВЕ-ЧЕТЫРЕ ТОЧКИ — СПИСОК БЕЗ ПОИСКА. Порог поиска взят из токенов
 * (BRANCH_SEARCH_FROM ← tokens.json → branchSwitcher.searchFrom), а не зашит здесь:
 * дизайнер подтвердил пять осознанно, вопреки «больше десятка» в ТЗ §4.4. Состояние
 * без поиска — не заглушка в ожидании макета: у салона с тремя точками поле поиска
 * над тремя строками выглядит нелепо и отнимает строку у самого списка.
 */

export function BranchSwitcher(props: {
  readonly branches: readonly CabinetBranchAccess[]
  readonly selection: BranchSelection
  readonly allowsAll: boolean
  /** Почему «Все филиалы» недоступны здесь. Пусто — доступны. */
  readonly allDeniedReason?: string | undefined
  readonly viewerTimeZone: string
  readonly now: Date
  readonly onPick: (branchId: string) => void
  /** Вид для узкого экрана: бордовая плашка вместо белой (макет владельца сети). */
  readonly filled?: boolean
}): ReactNode {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const close = useCallback(() => {
    setOpen(false)
    setQuery("")
  }, [])

  const current =
    props.selection.kind === "all"
      ? "Все филиалы"
      : props.selection.kind === "one"
        ? props.selection.branch.name
        : "Филиал не выбран"

  const found = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (needle === "") return props.branches
    return props.branches.filter((branch) => branch.name.toLowerCase().includes(needle))
  }, [props.branches, query])

  if (props.branches.length <= 1) {
    return (
      <span className={cx(styles.chip, props.filled ? styles.chipFilled : undefined)} title={current}>
        <span className={styles.chipLabel}>{current}</span>
      </span>
    )
  }

  const allDenied = !props.allowsAll || (props.allDeniedReason ?? "") !== ""

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={cx(styles.chip, styles.chipButton, props.filled ? styles.chipFilled : undefined)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={current}
        onClick={() => {
          setOpen((value) => !value)
        }}
      >
        <span className={styles.chipLabel}>{current}</span>
        <span className={styles.caret} aria-hidden="true">
          ▾
        </span>
        <span className="plnr-visually-hidden">Сменить филиал</span>
      </button>

      <Popover open={open} onClose={close} label="Выбор филиала" align="end">
        {props.branches.length >= BRANCH_SEARCH_FROM ? (
          <input
            type="search"
            className={styles.search}
            placeholder="Найти филиал"
            aria-label="Найти филиал"
            value={query}
            onChange={(event) => {
              setQuery(event.currentTarget.value)
            }}
          />
        ) : null}

        <button
          type="button"
          className={styles.all}
          disabled={allDenied}
          title={allDenied ? (props.allDeniedReason ?? "В этом разделе «Все филиалы» недоступны") : undefined}
          onClick={() => {
            props.onPick(ALL_BRANCHES)
            close()
          }}
        >
          <span>Все филиалы</span>
          <span className={styles.allNote}>
            {allDenied
              ? (props.allDeniedReason ?? "в этом разделе недоступно")
              : "сводка, поиск"}
          </span>
        </button>

        <div className={styles.list}>
          {found.map((branch) => {
            const chosen =
              props.selection.kind === "one" && props.selection.branch.id === branch.id
            const zone = formatShortZoneDiff(branch.timezone, props.viewerTimeZone, props.now)
            const sameZone = zone === "ваш пояс"
            return (
              <button
                key={branch.id}
                type="button"
                className={cx(styles.item, chosen ? styles.itemCurrent : undefined)}
                aria-current={chosen ? "true" : undefined}
                onClick={() => {
                  props.onPick(branch.id)
                  close()
                }}
              >
                <span className={styles.itemName}>{branch.name}</span>
                <span className={cx(styles.zone, sameZone ? styles.zoneSame : styles.zoneOther)}>
                  {zone}
                </span>
              </button>
            )
          })}
          {found.length === 0 ? (
            <p className={styles.empty}>Такого филиала нет. Проверьте написание названия точки.</p>
          ) : null}
        </div>
      </Popover>
    </div>
  )
}
